import { FC, useMemo } from 'react';

import { PiBoundingBox } from 'react-icons/pi';

import { AdElementVector, AdElementVectorColor, BrandVariable } from '@adflow/types';
import { Flex, Heading, Icon, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import VectorElementColorControl from './VectorElementColorControl';
import { useStore } from '../../../hooks/useStore';
import BrandBadge from '../BrandBadge';

type Props = {
  selectedElement: AdElementVector;
};

const VectorElementControls: FC<Props> = ({ selectedElement }) => {
  const store = useStore((state) => ({
    updateElementData: state.updateElementData,
    customFonts: state.customFonts,
    brandKit: state.brandKit,
  }));

  const updateFillColor = (fill: AdElementVectorColor) => {
    store.updateElementData(selectedElement.id, {
      ...selectedElement.data,
      color: {
        ...selectedElement.data.color,
        fill,
      },
    });
  };

  const updateStrokeColor = (stroke: AdElementVectorColor) => {
    store.updateElementData(selectedElement.id, {
      ...selectedElement.data,
      color: {
        ...selectedElement.data.color,
        stroke,
      },
    });
  };

  const stroke = useMemo(() => {
    return selectedElement.data.color.stroke;
  }, [selectedElement]);

  const fill = useMemo(() => {
    return selectedElement.data.color.fill;
  }, [selectedElement]);

  const defaultIndex = useMemo(() => {
    if (!fill && stroke) {
      return 1;
    }
    return 0;
  }, [fill, stroke]);

  // Check if fill or stroke color matches a brand color
  const fillBrandColorType = useMemo(() => {
    if (!store.brandKit || !fill?.value) return null;
    if (
      fill.value === BrandVariable.PRIMARY_COLOR ||
      fill.value === BrandVariable.SECONDARY_COLOR
    ) {
      return fill.value as BrandVariable;
    }
    return null;
  }, [store.brandKit, fill]);

  const strokeBrandColorType = useMemo(() => {
    if (!store.brandKit || !stroke?.value) return null;
    if (
      stroke.value === BrandVariable.PRIMARY_COLOR ||
      stroke.value === BrandVariable.SECONDARY_COLOR
    ) {
      return stroke.value as BrandVariable;
    }
    return null;
  }, [store.brandKit, stroke]);

  return (
    <Flex direction="column">
      <Flex alignItems="center" mb="6">
        <Icon as={PiBoundingBox} fontSize="xl" mr="2" />
        <Heading as="h2" size="md" noOfLines={1}>
          {selectedElement.name}
        </Heading>
      </Flex>
      <Tabs variant="enclosed" defaultIndex={defaultIndex}>
        <TabList>
          <Tab isDisabled={!fill}>
            <Flex alignItems="center" gap={2}>
              Fill Color
              {fillBrandColorType && <BrandBadge variableType={fillBrandColorType} />}
            </Flex>
          </Tab>
          <Tab isDisabled={!stroke}>
            <Flex alignItems="center" gap={2}>
              Stroke Color
              {strokeBrandColorType && <BrandBadge variableType={strokeBrandColorType} />}
            </Flex>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            {fill && (
              <VectorElementColorControl
                vectorColor={fill}
                onChange={updateFillColor}
                isBrandColor={fillBrandColorType !== null}
              />
            )}
          </TabPanel>
          <TabPanel>
            {stroke && (
              <VectorElementColorControl
                vectorColor={stroke}
                onChange={updateStrokeColor}
                isBrandColor={strokeBrandColorType !== null}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

VectorElementControls.displayName = 'VectorElementControls';

export default VectorElementControls;
